// pages/TrainingPage.jsx
// Landing page for the live online CAD automation training, routed at /training.
//
// Copy, section order, and layout come from training-page-sample.html at the
// repo root. That file stays in place as the reference, and every change here
// is mirrored there.
//
// Styling follows components/course/CourseSalesSections.jsx: static styling as
// inline style objects, and everything inline styles cannot express (hover,
// focus, media queries, keyframes, reduced motion) in the single tpg template
// literal at the bottom of the file.
//
// Palette is locked to five colors. Borders and muted text are alpha values of
// navy or cream rather than new hues, and white is used only as a card surface.
// Flat throughout: no shadows, no gradients, no radius above 4px.

import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { trainingApi } from "../api/training";
import {
  COUNTRY_CODES,
  FLAG_VIEWBOX,
  countryFor,
  dialFor,
} from "../data/countryDialCodes";
import "../styles/pages.css";

const C = {
  cream: "#F5F2EC",
  navy: "#1a1a2e",
  blue: "#2563EB",
  orange: "#EA580C",
  green: "#16A34A",
  white: "#FFFFFF",
  // Derived, so the palette stays at five colors.
  line: "rgba(26,26,46,0.14)",
  muted: "rgba(26,26,46,0.66)",
  creamText: "rgba(245,242,236,0.74)",
  creamBorder: "rgba(245,242,236,0.18)",
};

const serif = "'Fraunces', Georgia, serif";
const sans = "'Plus Jakarta Sans', system-ui, sans-serif";
const mono = "'JetBrains Mono', monospace";

/* ------------------------------------------------------------------ */
/* Copy, lifted from the reference file. Repeated items live in arrays  */
/* so the JSX below stays readable.                                     */
/* ------------------------------------------------------------------ */

const FACTS = [
  { k: "Format", v: "Live online" },
  { k: "Duration", v: "Up to 2 months" },
  { k: "Batch size", v: "20" },
  { k: "Language", v: "English" },
];

const GOOD_FIT = [
  "Work daily in SolidWorks and repeat the same modelling or drawing tasks",
  "Have never written a line of code, or have tried macros and want to go further",
  "Want a skill that is yours, whether that becomes a new role, freelance work, or your own product",
  "Can commit 4 to 6 hours a week including practice time",
];

const NOT_FIT = [
  "Are looking for a certificate without doing the exercises",
  "Cannot attend live sessions at the batch timing",
  "Do not have a laptop that can run your CAD software",
  "Want a guaranteed placement rather than a skill",
];

const GETS = [
  {
    num: "01",
    title: "Live sessions",
    body: "Every session is taught live, not a replay. You can ask questions while the code is on screen and get an answer in the same session.",
  },
  {
    num: "02",
    title: "Industry project guidance",
    body: "You build one real automation tool end to end, reviewed with you in the sessions. You pick a problem from your own work if you have one.",
  },
  {
    num: "03",
    title: "Resume preparation",
    body: "Your resume rewritten around your actual experience and targeted at CAD API roles, not a generic template.",
  },
  {
    num: "04",
    title: "Interview assistance",
    body: "The C# and CAD API questions that actually get asked in these interviews, and how to structure an answer that lands.",
  },
  {
    num: "05",
    title: "Free access to the recorded course and workbooks",
    body: "When the full ScriptedCAD recorded video course is released, you get access at no extra cost, along with the workbooks. Use them to revise after the live batch ends.",
  },
];

// Escaped so this file stays ASCII.
const CHECK = "\u2713";

// What you will learn. Two lists, written in the order they are taught. The
// list renders as a CSS multi column, so the order here is the order a reader
// meets going down the first column and then down the second.
const LEARN_BLOCKS = [
  {
    label: "Programming language",
    title: "What you will learn in C#",
    items: [
      "Set up Visual Studio and build your first C# application from scratch",
      "Understand what C# and the .NET Framework actually are",
      "Work confidently with variables, constants, scope, and type conversion",
      "Use operators and conditions to make your code make decisions",
      "Write loops that repeat work for you, for, foreach, while, and do while",
      "Handle sets of data with arrays and lists",
      "Understand strings, structs, and enums, and when each one is the right choice",
      "Know the difference between reference types and value types, and why it causes bugs",
      "Write classes with constructors, fields, properties, methods, and access modifiers",
      "Understand the four pillars of OOP, encapsulation, inheritance, polymorphism, and abstraction",
      "Use interfaces to write code that stays easy to change later",
      "Catch errors with try catch, and add logging so you can find problems fast",
      "Debug your own code instead of guessing what went wrong",
    ],
  },
  {
    label: "CAD API",
    title: "What you will learn in the SolidWorks API",
    items: [
      "Understand what the SolidWorks API is and how your code talks to SolidWorks",
      "Set up an API project, reference the interop assemblies, and connect to a running session",
      "Read the API object model and find what you need in the API documentation",
      "Use the macro recorder as a learning tool rather than a crutch",
      "Open, create, save, close, and export documents from code",
      "Select geometry and objects programmatically with the SelectionManager",
      "Create sketch entities, relations, and dimensions from code",
      "Create, modify, suppress, and delete features",
      "Navigate the FeatureManager tree and find features by name or type",
      "Read and write custom properties, including configuration specific ones",
      "Create and manage configurations, equations, and global variables",
      "Add, replace, remove, and traverse components in an assembly",
      "Work with mates and component transforms",
      "Automate drawings: sheets, views, dimensions, annotations, notes, and tables",
      "Generate a bill of materials and export drawings to PDF",
      "Build a SolidWorks add-in with your own commands in the CommandManager",
      "Handle errors and validation so other people can actually use your tool",
      "Design, build, and test a complete automation tool end to end",
    ],
  },
];

// Monday first, so the strip reads the way a week is written here.
const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];

const BATCHES = [
  {
    title: "Weekend batch",
    bestFor: "Built around a full time job",
    time: "7:00 to 10:00 AM",
    timeMeta: "IST, 3 hours",
    // Indexes into DAY_LETTERS.
    activeDays: [5, 6],
    // What the day strip says, for anyone who is not seeing the strip.
    daysText: "Runs on Saturday and Sunday.",
    seats: "20",
    why: "Two longer weekend sessions keep the training clear of your working week. You cover a block of ground in one sitting, then have the week to practise it before the next session.",
  },
];

const INCLUDES = [
  "All live sessions for the full batch",
  "Industry level project guidance and code review",
  "Resume preparation for CAD API roles",
  "Interview question preparation",
  "Free access to the recorded course and workbooks when released",
];

const DISCLAIMER = [
  "This is a training programme. It is not a placement service and no job, interview call, or salary outcome is guaranteed.",
  "Resume preparation and interview assistance are guidance. Applying, appearing, and performing in interviews is your responsibility.",
  "You need a laptop that can run your CAD software comfortably, along with a stable internet connection, for the live sessions and for practice.",
  "Results depend on the hours you put into the exercises and the project between sessions.",
  "Session recordings, if shared, are for enrolled participants only and must not be redistributed.",
  "Batch timings and the schedule may shift slightly. Any change will be communicated in advance.",
];

const FAQS = [
  {
    q: "I have never written code. Can I still join?",
    a: "Yes. Part 1 starts from opening Visual Studio for the first time. What you do need is CAD experience, because every example is built on CAD problems.",
  },
  {
    q: "What if I miss a live session?",
    a: "Sessions are recorded and shared with the batch. Missing one is fine. Missing several will make the project hard to finish on time.",
  },
  {
    q: "What do I need on my machine?",
    a: "A laptop that can run your CAD software comfortably, and Visual Studio, which is free for this kind of work. Nothing else.",
  },
  {
    q: "Which CAD platforms are covered?",
    a: "SolidWorks and AutoCAD are running now. If you work in a different CAD system, mention it in the form and it will be considered for an upcoming batch. The C# half is identical whichever platform you pick, and it is what makes the second platform much faster to learn later.",
  },
  {
    q: "How is this different from the recorded course?",
    a: "The recorded course is self paced. This is live, with your questions answered in the session, your project reviewed, and your resume worked on. Enrolling here also gets you the recorded course free when it is released.",
  },
];

const EXPERIENCE_OPTIONS = [
  "Student or fresher",
  "0 to 2 years",
  "2 to 5 years",
  "5 to 10 years",
  "More than 10 years",
];

const SOFTWARE_OPTIONS = [
  "SolidWorks",
  "AutoCAD",
  "Inventor",
  "Creo",
  "NX",
  "Revit",
  "Other",
];

const API_OPTIONS = [
  "SolidWorks API",
  "AutoCAD .NET API (request)",
  "Inventor API (request)",
  "Revit API (request)",
  "NX Open (request)",
  "Creo Toolkit (request)",
  "Other (mention below)",
];

/* ------------------------------------------------------------------ */
/* Shared bits                                                          */
/* ------------------------------------------------------------------ */

function Eyebrow({ children, tone = C.orange, dark = false }) {
  return (
    <span
      style={{
        display: "block",
        fontFamily: mono,
        fontSize: 12,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: dark ? "rgba(245,242,236,0.85)" : tone,
        marginBottom: 16,
      }}
    >
      {children}
    </span>
  );
}

function H2({ children, dark = false }) {
  return (
    <h2
      style={{
        fontFamily: serif,
        fontWeight: 600,
        fontSize: "clamp(24px, 4vw, 34px)",
        lineHeight: 1.15,
        letterSpacing: "-0.01em",
        color: dark ? C.cream : C.navy,
        marginBottom: 14,
      }}
    >
      {children}
    </h2>
  );
}

function Lede({ children, dark = false }) {
  return (
    <p
      style={{
        fontSize: 18,
        lineHeight: 1.65,
        color: dark ? C.creamText : C.muted,
        maxWidth: 640,
        marginBottom: 40,
      }}
    >
      {children}
    </p>
  );
}

// A flag from data/countryDialCodes.js. The hairline keeps a light flag from
// floating on the cream field.
function Flag({ country }) {
  return (
    <svg
      className="tpg-flag"
      viewBox={FLAG_VIEWBOX}
      width="20"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      {country.flag.map((sh, i) =>
        sh.r !== undefined ? (
          <circle key={i} cx={sh.cx} cy={sh.cy} r={sh.r} fill={sh.fill} />
        ) : (
          <rect
            key={i}
            x={sh.x}
            y={sh.y}
            width={sh.w}
            height={sh.h}
            fill={sh.fill}
            transform={sh.rot === undefined ? undefined : `rotate(${sh.rot} 10 7)`}
          />
        )
      )}
    </svg>
  );
}

function Chevron() {
  return (
    <svg
      className="tpg-chev"
      viewBox="0 0 10 6"
      width="10"
      height="6"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M1 1L5 5L9 1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Country dial code picker. A native select showed the whole country name and
// so had to be far too wide, and its flag emoji do not render on Windows, so
// this is a listbox of our own.
//
// aria-activedescendant belongs on the element that owns the active option, so
// it lives on the listbox and focus moves into the panel while it is open.
// The rows are moved over with the arrow keys and pointed at from the panel.
// Escape and selection both hand focus back to the trigger, and the ring on the
// shared field never flickers because .tpg-phone styles :focus-within, which
// holds for the trigger and the panel alike.
function CountrySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const selected = countryFor(value);
  const selectedIndex = COUNTRY_CODES.findIndex((c) => c.id === selected.id);

  // Clicking anywhere else closes the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  // The panel owns aria-activedescendant, so it has to hold focus to be the
  // element the arrow keys are read against.
  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  // Keep the highlighted row in view while arrowing through a scrolled panel.
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const row = panelRef.current.children[hi];
    if (row) row.scrollIntoView({ block: "nearest" });
  }, [open, hi]);

  // Closing always hands focus back to the trigger, so Escape and a chosen row
  // both leave the keyboard somewhere obvious.
  const close = () => {
    setOpen(false);
    if (btnRef.current) btnRef.current.focus();
  };

  const choose = (id) => {
    onChange(id);
    close();
  };

  const openAt = () => {
    setHi(selectedIndex < 0 ? 0 : selectedIndex);
    setOpen(true);
  };

  // The trigger only has to open. Enter and Space already reach openAt through
  // the button's own click, so only the arrows are handled here.
  const onBtnKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) openAt();
    }
  };

  // Everything while the panel is open, read against the panel because that is
  // where focus now sits.
  const onPanelKeyDown = (e) => {
    const { key } = e;
    if (key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (key === "ArrowDown" || key === "ArrowUp") {
      e.preventDefault();
      const step = key === "ArrowDown" ? 1 : -1;
      setHi((i) => Math.min(COUNTRY_CODES.length - 1, Math.max(0, i + step)));
      return;
    }
    if (key === "Home" || key === "End") {
      e.preventDefault();
      setHi(key === "Home" ? 0 : COUNTRY_CODES.length - 1);
      return;
    }
    if (key === "Enter" || key === " " || key === "Spacebar") {
      e.preventDefault();
      choose(COUNTRY_CODES[hi].id);
    }
  };

  return (
    <div className="tpg-cc" ref={wrapRef}>
      <button
        type="button"
        ref={btnRef}
        className="tpg-cc-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="tpg-cc-panel"
        aria-label={`Country dial code, ${selected.name} ${selected.dial}`}
        onClick={() => (open ? setOpen(false) : openAt())}
        onKeyDown={onBtnKeyDown}
      >
        <Flag country={selected} />
        <span className="tpg-cc-dial">{selected.dial}</span>
        <Chevron />
      </button>

      {open && (
        <div
          id="tpg-cc-panel"
          className="tpg-cc-panel"
          role="listbox"
          ref={panelRef}
          tabIndex={-1}
          aria-label="Country dial code"
          aria-activedescendant={`tpg-cc-opt-${COUNTRY_CODES[hi].id}`}
          onKeyDown={onPanelKeyDown}
          onBlur={() => setOpen(false)}
        >
          {COUNTRY_CODES.map((c, i) => (
            <div
              key={c.id}
              id={`tpg-cc-opt-${c.id}`}
              role="option"
              aria-selected={c.id === selected.id}
              className={
                i === hi ? "tpg-cc-row tpg-cc-row--hi" : "tpg-cc-row"
              }
              // Keeps focus on the panel, so the field never loses its ring.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHi(i)}
              onClick={() => choose(c.id)}
            >
              <Flag country={c} />
              <span className="tpg-cc-name">{c.name}</span>
              <span className="tpg-cc-code">{c.dial}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The line under a control: its error if it has one, otherwise its hint.
// Renders nothing when there is neither, so an empty field adds no gap.
function FieldNote({ id, error, hint }) {
  if (error) {
    return (
      <span id={id} style={errorStyle}>
        {error}
      </span>
    );
  }
  if (hint) {
    return (
      <span id={id} style={hintStyle}>
        {hint}
      </span>
    );
  }
  return null;
}

// Flat button, used as an anchor for the two in page CTAs.
function CtaLink({ href, children, variant = "primary", dark = false }) {
  const base = {
    display: "inline-block",
    padding: "14px 26px",
    borderRadius: 4,
    fontFamily: sans,
    fontWeight: 600,
    fontSize: 16,
    textDecoration: "none",
    border: `1px solid ${dark ? C.cream : C.navy}`,
    cursor: "pointer",
  };
  const skin =
    variant === "primary"
      ? { background: dark ? C.cream : C.navy, color: dark ? C.navy : C.cream }
      : { background: "transparent", color: dark ? C.cream : C.navy };
  return (
    <a
      href={href}
      className={`tpg-btn tpg-btn--${variant}${dark ? " tpg-btn--dark" : ""}`}
      style={{ ...base, ...skin }}
    >
      {children}
    </a>
  );
}

// One block of outcomes: a bordered card on white, with a mono label over a
// Fraunces heading and the checklist below. The list is a CSS multi column
// rather than a grid, so items fill the first column before the second and
// keep the order they are written in.
function LearnBlock({ label, title, items }) {
  return (
    <div className="tpg-learn">
      <span
        style={{
          display: "block",
          fontFamily: mono,
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: C.blue,
          marginBottom: 8,
        }}
      >
        {label}
      </span>
      <h3
        style={{
          fontFamily: serif,
          fontWeight: 600,
          fontSize: "clamp(21px, 2.8vw, 26px)",
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          color: C.navy,
          marginBottom: 22,
        }}
      >
        {title}
      </h3>
      <ul className="tpg-learn-list">
        {items.map((item) => (
          <li key={item} className="tpg-learn-item">
            {/* Absolute, so the mark sits on the first line of a wrapping
                item rather than centring itself against the whole item. */}
            <span className="tpg-check" aria-hidden="true">
              {CHECK}
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

// Hint copy, kept beside the validation rules because a field's note slot
// shows one or the other: an error replaces the hint while it stands.
const HINTS = {
  phone:
    "This is how our team will contact you, so please make sure it is on WhatsApp.",
  platform:
    "SolidWorks batches are running now. Anything else is taken as a request for an upcoming batch.",
  goal: "Optional. A real task from your work helps me shape your project.",
};

const PHONE_ERROR = "Please enter a valid phone number, digits only.";

// Client side validation, in addition to the backend checks rather than in
// place of them. Each rule returns an empty string when the field is good.
const VALIDATORS = {
  name: (f) =>
    f.name.trim().length >= 2 ? "" : "Please enter your full name.",
  email: (f) =>
    // Text either side of the at sign, and a dot in the domain.
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())
      ? ""
      : "Please enter a valid email address.",
  phone: (f) => {
    const raw = f.phone.trim();
    // Digits, spaces, and hyphens only. Anything with a letter in it fails
    // here rather than being quietly passed on to the backend.
    if (!/^[0-9\s-]+$/.test(raw)) return PHONE_ERROR;
    const digits = raw.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15 ? "" : PHONE_ERROR;
  },
  experience: (f) =>
    f.experience ? "" : "Please select your experience.",
  software: (f) =>
    f.software ? "" : "Please select the CAD software you use.",
  platform: (f) =>
    f.platform ? "" : "Please select what you want to learn.",
  consent: (f) =>
    f.consent ? "" : "Please accept the disclaimer to continue.",
};

// Document order, so submitting an empty form focuses the topmost problem.
const FIELD_ORDER = [
  "name",
  "email",
  "phone",
  "experience",
  "software",
  "platform",
  "consent",
];

// Where focus goes for each field.
const FOCUS_ID = {
  name: "tpg-name",
  email: "tpg-email",
  phone: "tpg-phone",
  experience: "tpg-experience",
  software: "tpg-software",
  platform: "tpg-platform",
  consent: "tpg-consent",
};

const noteId = (key) => `tpg-${key}-note`;

const EMPTY_FORM = {
  name: "",
  email: "",
  // Country behind the dial code selector. It is joined onto phone on submit
  // and never travels as a field of its own.
  country: "IN",
  phone: "",
  experience: "",
  software: "",
  platform: "",
  goal: "",
  consent: false,
};

const labelStyle = {
  display: "block",
  fontWeight: 600,
  fontSize: 15,
  marginBottom: 8,
  color: C.navy,
};

// Helper text, rendered under its control. No bottom margin: the row grid
// owns the spacing between fields, so a hint must not add to it.
const hintStyle = {
  display: "block",
  fontWeight: 400,
  color: C.muted,
  fontSize: 14,
  lineHeight: 1.5,
  marginTop: 8,
};

// The same slot as the hint, in orange. An error replaces the hint rather
// than stacking under it, so a field never grows as it goes invalid.
const errorStyle = {
  ...hintStyle,
  color: C.orange,
};

// Every control is pinned to one height rather than left to size itself off
// its own line box, so a select, an input, and the bordered WhatsApp wrapper
// all come out identical. Borders are inside the box, so the wrapper's own
// border does not make it a pixel taller than a plain input.
const FIELD_H = 48;

const inputStyle = {
  width: "100%",
  height: FIELD_H,
  padding: "12px 14px",
  border: `1px solid ${C.line}`,
  borderRadius: 4,
  fontFamily: sans,
  fontSize: 16,
  background: C.cream,
  color: C.navy,
};

// The dial code picker and the number share one border, drawn by the
// .tpg-phone wrapper, so the pair reads as a single field.
const phoneInputStyle = {
  ...inputStyle,
  flex: "1 1 auto",
  minWidth: 0,
  // The wrapper carries the height, this stretches to it.
  height: "auto",
  border: "none",
  borderRadius: 0,
  background: "transparent",
};

export default function TrainingPage() {
  const [form, setForm] = useState(EMPTY_FORM);
  // idle | submitting | done | error
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  // Per field messages, keyed the same as the form. A key is present only
  // once that field has been touched and has actually failed.
  const [errors, setErrors] = useState({});

  const check = (key, f) => (VALIDATORS[key] ? VALIDATORS[key](f) : "");

  // Blur is the first moment a field is allowed to complain, so nobody is
  // told they are wrong while still typing their first character.
  const touch = (key) => () =>
    setErrors((prev) => {
      const msg = check(key, form);
      if (msg === (prev[key] || "")) return prev;
      const next = { ...prev, [key]: msg };
      if (!msg) delete next[key];
      return next;
    });

  const set = (key) => (e) => {
    const el = e.target;
    const value = el.type === "checkbox" ? el.checked : el.value;
    const next = { ...form, [key]: value };
    setForm(next);

    // A choice is made in one gesture, so it is judged as soon as it changes.
    // Everything else re-checks only once it has already shown an error, so
    // typing towards a valid value clears the message without nagging.
    const live = el.type === "checkbox" || el.type === "radio";
    setErrors((prev) => {
      if (!live && !prev[key]) return prev;
      const msg = check(key, next);
      if (msg === (prev[key] || "")) return prev;
      const merged = { ...prev, [key]: msg };
      if (!msg) delete merged[key];
      return merged;
    });
  };

  // Props shared by every control: the error wiring and the blur trigger.
  const fieldProps = (key) => ({
    onBlur: touch(key),
    "aria-invalid": errors[key] ? "true" : undefined,
    "aria-describedby":
      errors[key] || HINTS[key] ? noteId(key) : undefined,
  });

  // Orange border while a field is in error, over the shared control style.
  const controlStyle = (key) =>
    errors[key] ? { ...inputStyle, borderColor: C.orange } : inputStyle;

  const fail = (text) => {
    setStatus("error");
    setMessage(text);
  };

  // POST to /api/training-lead. On success the whole form is replaced by the
  // confirmation block. Nothing here navigates.
  const onSubmit = async (e) => {
    e.preventDefault();

    // Everything at once, so the person sees the whole list rather than
    // being walked through it one refusal at a time.
    const found = {};
    FIELD_ORDER.forEach((key) => {
      const msg = check(key, form);
      if (msg) found[key] = msg;
    });

    const firstBad = FIELD_ORDER.find((key) => found[key]);
    if (firstBad) {
      setErrors(found);
      // The backend message and the field messages are separate systems.
      // Clear the old backend one so the form does not show a stale failure
      // alongside fresh field errors.
      setStatus("idle");
      setMessage("");
      const el = document.getElementById(FOCUS_ID[firstBad]);
      if (el) el.focus();
      return;
    }

    setErrors({});

    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();

    setStatus("submitting");
    setMessage("");
    try {
      await trainingApi.requestSeat({
        name,
        email,
        phone: `${dialFor(form.country)}${phone}`,
        experience: form.experience,
        software: form.software,
        platform: form.platform,
        batch: "weekend",
        goal: form.goal.trim() || null,
        consent: true,
        source: "training-page",
      });
      setStatus("done");
    } catch (err) {
      fail(err.message || "Could not send your request. Please try again.");
    }
  };

  // The confirmation replaces the form, and the form is much taller, so the
  // page shortens sharply and leaves the visitor scrolled below the result.
  const doneRef = useRef(null);
  // Latched so a later re-render in the done state cannot yank the page back.
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (status !== "done") {
      scrolledRef.current = false;
      return;
    }
    if (scrolledRef.current) return;
    const el = doneRef.current;
    if (!el) return;
    scrolledRef.current = true;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({
      block: "center",
      behavior: reduced ? "auto" : "smooth",
    });
    // Keyboard users land on the confirmation rather than somewhere in the
    // collapsed page. preventScroll so the focus does not fight the animation.
    el.focus({ preventScroll: true });
  }, [status]);

  const submitting = status === "submitting";

  return (
    <>
      <Navbar />

      <main
        className="tpg-main"
        style={{ background: C.cream, color: C.navy, fontFamily: sans, lineHeight: 1.65 }}
      >
        {/* Hero */}
        <header style={{ background: C.cream, borderBottom: `1px solid ${C.line}` }}>
          <div className="tpg-wrap tpg-sec tpg-sec--hero">
            <div className="tpg-fade">
              <Eyebrow>Live online training / Next batch October 2026</Eyebrow>
              <h1
                style={{
                  fontFamily: serif,
                  fontWeight: 600,
                  fontSize: "clamp(30px, 6.2vw, 54px)",
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  maxWidth: "15ch",
                  marginBottom: 22,
                }}
              >
                Learn CAD automation from an automation expert
              </h1>
              <p
                style={{
                  fontSize: 19,
                  color: C.muted,
                  maxWidth: 620,
                  marginBottom: 34,
                }}
              >
                C# from basics to intermediate, then the complete CAD API. Taught
                live, in small batches, with a real project you can put on your
                resume. Running now for SolidWorks and AutoCAD.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  flexWrap: "wrap",
                  marginBottom: 48,
                }}
              >
                <CtaLink href="#apply">Request a seat</CtaLink>
                <CtaLink href="#syllabus" variant="ghost">
                  See the syllabus
                </CtaLink>
              </div>
            </div>

            {/* A plain row on the cream ground. The hairlines between pairs
                come from the stylesheet, so there is no box and no surface. */}
            <div className="tpg-facts tpg-fade tpg-fade-2">
              {FACTS.map((f) => (
                <div key={f.k} className="tpg-fact">
                  <span
                    style={{
                      display: "block",
                      fontFamily: mono,
                      fontSize: 11,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: C.muted,
                      marginBottom: 6,
                    }}
                  >
                    {f.k}
                  </span>
                  <span style={{ fontWeight: 600, fontSize: 16 }}>{f.v}</span>
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* Who this is for */}
        <section id="who" style={{ background: C.cream, borderBottom: `1px solid ${C.line}` }}>
          <div className="tpg-wrap tpg-sec">
            <Eyebrow tone={C.blue}>Who this is for</Eyebrow>
            <H2>Design engineers who are tired of repeating the same clicks</H2>
            <Lede>
              This training assumes you know CAD. It does not assume you know how
              to code.
            </Lede>
            <div className="tpg-two">
              <div>
                <h3
                  style={{
                    fontFamily: serif,
                    fontWeight: 600,
                    fontSize: 19,
                    lineHeight: 1.15,
                    marginBottom: 12,
                  }}
                >
                  A good fit if you
                </h3>
                <ul style={{ listStyle: "none" }}>
                  {GOOD_FIT.map((item) => (
                    <li key={item} style={fitItemStyle}>
                      <span style={{ ...fitMarkStyle, background: C.blue }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3
                  style={{
                    fontFamily: serif,
                    fontWeight: 600,
                    fontSize: 19,
                    lineHeight: 1.15,
                    marginBottom: 12,
                  }}
                >
                  Not a good fit if you
                </h3>
                <ul style={{ listStyle: "none" }}>
                  {NOT_FIT.map((item) => (
                    <li key={item} style={fitItemStyle}>
                      <span style={{ ...fitMarkStyle, background: C.orange }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* What you get */}
        <section id="get" style={{ background: C.navy }}>
          <div className="tpg-wrap tpg-sec">
            <Eyebrow dark>What you get</Eyebrow>
            <H2 dark>Five things, all included</H2>
            {/* Stacked entries rather than a grid of cells. The numeral sits
                beside the text and a hairline separates one from the next. */}
            <div className="tpg-gets">
              {GETS.map((g) => (
                <div key={g.num} className="tpg-get">
                  <span
                    style={{
                      fontFamily: serif,
                      fontWeight: 600,
                      fontSize: "clamp(34px, 5vw, 46px)",
                      lineHeight: 1,
                      letterSpacing: "-0.02em",
                      color: C.green,
                    }}
                  >
                    {g.num}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <h3
                      style={{
                        fontFamily: serif,
                        fontWeight: 600,
                        fontSize: 21,
                        lineHeight: 1.15,
                        color: C.cream,
                        marginBottom: 8,
                      }}
                    >
                      {g.title}
                    </h3>
                    <p style={{ color: C.creamText, fontSize: 15.5, maxWidth: 620 }}>
                      {g.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What you will learn. Two bordered blocks, each a two column
            checklist that collapses to one column below 920px. */}
        <section id="syllabus" style={{ background: C.cream, borderBottom: `1px solid ${C.line}` }}>
          <div className="tpg-wrap tpg-sec">
            <Eyebrow tone={C.blue}>What you will learn</Eyebrow>
            <H2>Two skills, taught in order</H2>
            <Lede>
              The language first, then the API. Skipping the language is the
              most common reason people stall on CAD automation.
            </Lede>

            {LEARN_BLOCKS.map((b) => (
              <LearnBlock key={b.label} {...b} />
            ))}
          </div>
        </section>

        {/* Batches */}
        <section id="batches" style={{ background: C.navy }}>
          <div className="tpg-wrap tpg-sec">
            <Eyebrow dark>Batches</Eyebrow>
            <H2 dark>When the sessions run</H2>
            <Lede dark>
              All timings are India Standard Time. Seats are capped so that every
              person can ask questions in a session.
            </Lede>
            <div className="tpg-batches">
              {BATCHES.map((b) => (
                <div
                  key={b.title}
                  style={{
                    border: `1px solid ${C.creamBorder}`,
                    background: C.navy,
                  }}
                  className="tpg-box"
                >
                  {/* Zone 1, what it is. The badge sits inline so it hugs its
                      own text rather than ruling across the card. */}
                  {b.recommended ? (
                    <span
                      style={{
                        display: "inline-block",
                        fontFamily: mono,
                        fontSize: 11,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        lineHeight: 1,
                        color: C.green,
                        border: `1px solid ${C.green}`,
                        borderRadius: 2,
                        padding: "5px 8px",
                        marginBottom: 14,
                      }}
                    >
                      Recommended
                    </span>
                  ) : null}
                  <h3
                    style={{
                      fontFamily: serif,
                      fontWeight: 600,
                      fontSize: "clamp(24px, 3.4vw, 30px)",
                      lineHeight: 1.15,
                      letterSpacing: "-0.01em",
                      color: C.cream,
                      marginBottom: 6,
                    }}
                  >
                    {b.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: mono,
                      fontSize: 13,
                      color: C.creamText,
                      marginBottom: 30,
                    }}
                  >
                    {b.bestFor}
                  </p>

                  {/* Zone 2, when it runs. The line the card is built around. */}
                  <div style={{ marginBottom: 30 }}>
                    <div
                      style={{
                        fontFamily: mono,
                        fontSize: "clamp(26px, 4vw, 34px)",
                        lineHeight: 1.1,
                        letterSpacing: "-0.03em",
                        color: C.cream,
                      }}
                    >
                      {b.time}
                    </div>
                    <span style={metaLineStyle}>{b.timeMeta}</span>
                  </div>

                  {/* Zone 3, which days. Decorative and duplicated by the
                      sentence that follows it, so it is hidden from assistive
                      tech rather than read out as seven stray letters. */}
                  <div className="tpg-days" aria-hidden="true">
                    {DAY_LETTERS.map((d, i) => (
                      <span
                        key={`${d}${i}`}
                        className={
                          b.activeDays.includes(i) ? "tpg-day tpg-day--on" : "tpg-day"
                        }
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                  <span className="tpg-sr">{b.daysText}</span>

                  {/* Zone 4, how many seats. The one rule on the card. */}
                  <div
                    style={{
                      borderTop: `1px solid ${C.creamBorder}`,
                      marginTop: 30,
                      paddingTop: 24,
                    }}
                  >
                    <p style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span
                        style={{
                          fontFamily: serif,
                          fontWeight: 600,
                          fontSize: 30,
                          lineHeight: 1,
                          color: C.orange,
                        }}
                      >
                        {b.seats}
                      </span>
                      <span style={{ fontSize: 16, color: C.cream }}>seats</span>
                    </p>
                    <span style={metaLineStyle}>
                      Maximum, to keep sessions interactive
                    </span>
                  </div>

                  {/* Zone 5, why you would pick this one. Same hairline as the
                      seats block above it. */}
                  <div
                    style={{
                      borderTop: `1px solid ${C.creamBorder}`,
                      marginTop: 30,
                      paddingTop: 24,
                    }}
                  >
                    <p style={{ fontSize: 15.5, color: C.creamText }}>{b.why}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What a seat includes */}
        <section id="included" style={{ background: C.cream, borderBottom: `1px solid ${C.line}` }}>
          <div className="tpg-wrap tpg-narrow tpg-sec">
            <Eyebrow>What a seat includes</Eyebrow>
            <H2>Everything included</H2>
            <div
              style={{
                border: `1px solid ${C.navy}`,
                background: C.white,
                textAlign: "center",
              }}
              className="tpg-box tpg-box--lg"
            >
              <p
                style={{
                  color: C.muted,
                  fontSize: 15.5,
                  maxWidth: 420,
                  margin: "0 auto",
                }}
              >
                Request a seat and our team will reach out to you on WhatsApp to
                talk through the training and answer anything you want to know
                before you decide.
              </p>
              <ul
                style={{
                  listStyle: "none",
                  margin: "28px auto 0",
                  maxWidth: 420,
                  textAlign: "left",
                }}
              >
                {INCLUDES.map((item, i) => (
                  <li
                    key={item}
                    style={{
                      display: "flex",
                      gap: 12,
                      padding: "9px 0",
                      // Last item drops its rule so the list closes on the
                      // card edge rather than on a stray hairline.
                      borderBottom:
                        i === INCLUDES.length - 1
                          ? "none"
                          : `1px solid ${C.line}`,
                      fontSize: 15.5,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: mono,
                        fontWeight: 700,
                        color: C.green,
                        flexShrink: 0,
                      }}
                    >
                      +
                    </span>
                    <span style={{ minWidth: 0 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 30 }}>
                <CtaLink href="#apply">Request a seat</CtaLink>
              </div>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section id="disclaimer" style={{ background: C.cream, borderBottom: `1px solid ${C.line}` }}>
          <div className="tpg-wrap tpg-narrow tpg-sec">
            <Eyebrow>Please read</Eyebrow>
            <H2>Disclaimer</H2>
            <div
              style={{
                border: `1px solid ${C.orange}`,
                background: C.white,
              }}
              className="tpg-box"
            >
              <h3
                style={{
                  fontFamily: serif,
                  fontWeight: 600,
                  fontSize: 20,
                  lineHeight: 1.15,
                  marginBottom: 14,
                }}
              >
                What this training does and does not promise
              </h3>
              <ol
                style={{
                  paddingLeft: 20,
                  color: C.muted,
                  fontSize: 15.5,
                }}
              >
                {DISCLAIMER.map((item) => (
                  <li key={item} style={{ padding: "6px 0" }}>
                    {item}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* Request a seat */}
        <section id="apply" style={{ background: C.navy }}>
          <div className="tpg-wrap tpg-narrow tpg-sec">
            <Eyebrow dark>Request a seat</Eyebrow>
            <H2 dark>Tell me a bit about yourself</H2>
            <Lede dark>
              Fill this in and our team will reach out to you on WhatsApp
              regarding the next steps. Takes about a minute.
            </Lede>

            {status === "done" ? (
              <div
                ref={doneRef}
                role="status"
                aria-live="polite"
                tabIndex={-1}
                style={{
                  border: `1px solid ${C.green}`,
                  background: C.white,
                }}
                className="tpg-box"
              >
                <h3
                  style={{
                    fontFamily: serif,
                    fontWeight: 600,
                    fontSize: 22,
                    lineHeight: 1.15,
                    color: C.green,
                    marginBottom: 10,
                  }}
                >
                  Request received
                </h3>
                <p style={{ color: C.muted, fontSize: 15.5 }}>
                  Your response has been recorded. Our team will reach out to
                  you on WhatsApp to discuss the training and the next steps.
                </p>
              </div>
            ) : (
              <div
                style={{
                  border: `1px solid ${C.creamBorder}`,
                  background: C.white,
                }}
                className="tpg-box tpg-box--lg"
              >
                <form onSubmit={onSubmit} noValidate>
                  <div className="tpg-row">
                    <div className="tpg-field">
                      <label htmlFor="tpg-name" style={labelStyle}>
                        Full name
                      </label>
                      <input
                        className="tpg-input"
                        type="text"
                        id="tpg-name"
                        name="name"
                        value={form.name}
                        onChange={set("name")}
                        required
                        style={controlStyle("name")}
                        {...fieldProps("name")}
                      />
                      <FieldNote id={noteId("name")} error={errors.name} />
                    </div>
                    <div className="tpg-field">
                      <label htmlFor="tpg-email" style={labelStyle}>
                        Email
                      </label>
                      <input
                        className="tpg-input"
                        type="email"
                        id="tpg-email"
                        name="email"
                        value={form.email}
                        onChange={set("email")}
                        required
                        style={controlStyle("email")}
                        {...fieldProps("email")}
                      />
                      <FieldNote id={noteId("email")} error={errors.email} />
                    </div>
                  </div>

                  <div className="tpg-row">
                    <div className="tpg-field">
                      <label htmlFor="tpg-phone" style={labelStyle}>
                        WhatsApp number
                      </label>
                      {/* The border lives on the wrapper, so the error colour
                          goes there rather than on the input inside it. */}
                      <div
                        className={
                          errors.phone ? "tpg-phone tpg-phone--err" : "tpg-phone"
                        }
                      >
                        <CountrySelect
                          value={form.country}
                          onChange={(id) =>
                            setForm((f) => ({ ...f, country: id }))
                          }
                        />
                        <input
                          type="tel"
                          id="tpg-phone"
                          name="phone"
                          value={form.phone}
                          onChange={set("phone")}
                          required
                          style={phoneInputStyle}
                          {...fieldProps("phone")}
                        />
                      </div>
                      <FieldNote
                        id={noteId("phone")}
                        error={errors.phone}
                        hint={HINTS.phone}
                      />
                    </div>
                    <div className="tpg-field">
                      <label htmlFor="tpg-experience" style={labelStyle}>
                        Years of design experience
                      </label>
                      <select
                        className="tpg-input"
                        id="tpg-experience"
                        name="experience"
                        value={form.experience}
                        onChange={set("experience")}
                        required
                        style={controlStyle("experience")}
                        {...fieldProps("experience")}
                      >
                        <option value="">Select</option>
                        {EXPERIENCE_OPTIONS.map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </select>
                      <FieldNote
                        id={noteId("experience")}
                        error={errors.experience}
                      />
                    </div>
                  </div>

                  <div className="tpg-field">
                    <label htmlFor="tpg-software" style={labelStyle}>
                      CAD software you use daily
                    </label>
                    <select
                      className="tpg-input"
                      id="tpg-software"
                      name="software"
                      value={form.software}
                      onChange={set("software")}
                      required
                      style={controlStyle("software")}
                      {...fieldProps("software")}
                    >
                      <option value="">Select</option>
                      {SOFTWARE_OPTIONS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                    <FieldNote id={noteId("software")} error={errors.software} />
                  </div>

                  <div className="tpg-field">
                    <label htmlFor="tpg-platform" style={labelStyle}>
                      Which CAD API do you want to learn?
                    </label>
                    <select
                      className="tpg-input"
                      id="tpg-platform"
                      name="platform"
                      value={form.platform}
                      onChange={set("platform")}
                      required
                      style={controlStyle("platform")}
                      {...fieldProps("platform")}
                    >
                      <option value="">Select</option>
                      {API_OPTIONS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                    <FieldNote
                      id={noteId("platform")}
                      error={errors.platform}
                      hint={HINTS.platform}
                    />
                  </div>

                  <div className="tpg-field">
                    <label htmlFor="tpg-goal" style={labelStyle}>
                      What do you want to automate first?
                    </label>
                    <textarea
                      className="tpg-input"
                      id="tpg-goal"
                      name="goal"
                      value={form.goal}
                      onChange={set("goal")}
                      placeholder="Example: exporting drawings to PDF with the right filename for 200 parts"
                      aria-describedby={noteId("goal")}
                      style={{
                        ...inputStyle,
                        height: "auto",
                        minHeight: 96,
                        resize: "vertical",
                      }}
                    />
                    <FieldNote id={noteId("goal")} hint={HINTS.goal} />
                  </div>

                  <div className="tpg-field">
                    <label
                      className="tpg-consent"
                      htmlFor="tpg-consent"
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        fontSize: 14.5,
                        color: C.muted,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="tpg-consent"
                        name="consent"
                        checked={form.consent}
                        onChange={set("consent")}
                        required
                        {...fieldProps("consent")}
                      />
                      <span style={{ minWidth: 0 }}>
                        I have read the disclaimer above and agree to be
                        contacted on WhatsApp about this training.
                      </span>
                    </label>
                    <FieldNote id={noteId("consent")} error={errors.consent} />
                  </div>

                  {status === "error" && (
                    <p
                      role="alert"
                      style={{
                        border: `1px solid ${C.orange}`,
                        borderRadius: 4,
                        padding: "12px 14px",
                        marginBottom: 18,
                        fontSize: 14.5,
                        color: C.orange,
                      }}
                    >
                      {message}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="tpg-btn tpg-btn--primary"
                    disabled={submitting}
                    style={{
                      display: "inline-block",
                      padding: "14px 26px",
                      borderRadius: 4,
                      fontFamily: sans,
                      fontWeight: 600,
                      fontSize: 16,
                      border: `1px solid ${C.navy}`,
                      background: C.navy,
                      color: C.cream,
                      cursor: submitting ? "default" : "pointer",
                      opacity: submitting ? 0.7 : 1,
                    }}
                  >
                    {submitting ? "Sending your request..." : "Request a seat"}
                  </button>
                  <p style={{ fontSize: 14, color: C.muted, marginTop: 14 }}>
                    Our team will reach out to you on WhatsApp. Your details are
                    used only for this training enquiry.
                  </p>
                </form>
              </div>
            )}
          </div>
        </section>

        {/* FAQ */}
        <section style={{ background: C.cream }}>
          <div className="tpg-wrap tpg-narrow tpg-sec">
            <Eyebrow tone={C.blue}>Questions</Eyebrow>
            <H2>Before you ask</H2>
            <div style={{ borderTop: `1px solid ${C.line}` }}>
              {FAQS.map((f) => (
                <details key={f.q} className="tpg-faq">
                  <summary className="tpg-sum tpg-sum--faq">
                    <span style={{ fontWeight: 600, fontSize: 17, minWidth: 0 }}>
                      {f.q}
                    </span>
                  </summary>
                  <p
                    style={{
                      color: C.muted,
                      marginTop: 12,
                      fontSize: 15.5,
                      padding: "0 20px 4px 20px",
                    }}
                  >
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style>{tpg}</style>
    </>
  );
}

// The small mono line that sits under the time and under the seat count.
const metaLineStyle = {
  display: "block",
  fontFamily: mono,
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: C.creamText,
  marginTop: 8,
};

// Shared bits for the two fit lists, kept out of the map so the rule reads once.
const fitItemStyle = {
  position: "relative",
  padding: "12px 0 12px 30px",
  borderBottom: `1px solid ${C.line}`,
  fontSize: 16,
  lineHeight: 1.55,
};

const fitMarkStyle = {
  position: "absolute",
  left: 0,
  top: 21,
  width: 14,
  height: 2,
};

const tpg = `
  .tpg-wrap{max-width:1080px;margin:0 auto;}
  .tpg-narrow{max-width:760px;}
  .tpg-main{font-size:17px;}

  /* Section rhythm. Responsive, so it lives here rather than inline. */
  .tpg-sec{padding:72px 24px;}
  .tpg-sec--hero{padding:84px 24px 72px;}

  /* Smooth anchor scrolling for the in page CTAs, switched off below for
     anyone who asked for reduced motion. */
  html,body{scroll-behavior:smooth;}

  .tpg-fade{animation:tpg-up .7s ease both;}
  .tpg-fade-2{animation-delay:.15s;}
  @keyframes tpg-up{from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:none;}}

  .tpg-btn{transition:background .2s,color .2s,border-color .2s;}
  .tpg-btn--primary:hover{background:#2563EB;border-color:#2563EB;color:#FFFFFF;}
  .tpg-btn--ghost:hover{background:#1a1a2e;color:#F5F2EC;}
  .tpg-btn--dark.tpg-btn--ghost:hover{background:#F5F2EC;color:#1a1a2e;}
  .tpg-btn:focus-visible{outline:2px solid #2563EB;outline-offset:2px;}
  .tpg-btn:disabled{cursor:default;}
  .tpg-btn:disabled:hover{background:#1a1a2e;border-color:#1a1a2e;color:#F5F2EC;}

  /* Hero facts. One row straight on the cream ground: no border around the
     strip, no surface under it, just a hairline between each pair. */
  .tpg-facts{display:grid;grid-template-columns:repeat(4,1fr);}
  .tpg-fact{padding:2px 22px;}
  .tpg-fact:first-child{padding-left:0;}
  .tpg-fact + .tpg-fact{border-left:1px solid rgba(26,26,46,0.14);}

  /* What you get. Stacked entries, numeral in the first column, hairline
     between entries and nothing enclosing them. */
  .tpg-gets{display:block;}
  .tpg-get{display:grid;grid-template-columns:auto 1fr;column-gap:28px;padding:30px 0;}
  .tpg-get:first-child{padding-top:0;}
  .tpg-get:last-child{padding-bottom:0;}
  .tpg-get + .tpg-get{border-top:1px solid rgba(245,242,236,0.18);}

  /* Batch day strip. Seven equal squares that never wrap: the grid holds the
     row at every width and the cells shrink instead. */
  .tpg-days{display:grid;grid-template-columns:repeat(7,1fr);gap:6px;max-width:280px;}
  .tpg-day{aspect-ratio:1;display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-size:12px;border-radius:2px;border:1px solid rgba(245,242,236,0.18);color:rgba(245,242,236,0.45);}
  .tpg-day--on{background:#F5F2EC;border-color:#F5F2EC;color:#1a1a2e;font-weight:500;}

  /* What you will learn. column-count rather than a grid, because the items
     have to fill the first column before the second, in the order given. */
  .tpg-learn{border:1px solid rgba(26,26,46,0.14);background:#FFFFFF;padding:30px;margin-bottom:20px;}
  .tpg-learn:last-child{margin-bottom:0;}
  .tpg-learn-list{list-style:none;column-count:2;column-gap:40px;}
  .tpg-learn-item{position:relative;padding-left:28px;margin-bottom:14px;font-size:16px;line-height:1.55;color:#1a1a2e;break-inside:avoid;-webkit-column-break-inside:avoid;}
  .tpg-check{position:absolute;left:0;top:0;color:#2563EB;font-weight:700;}

  .tpg-box{padding:30px;}
  .tpg-box--lg{padding:36px;}

  .tpg-two{display:grid;grid-template-columns:1fr 1fr;gap:40px;}
  .tpg-batches{display:grid;grid-template-columns:1fr;gap:20px;max-width:560px;}
  /* Form rows. The row owns three tracks, label then control then hint, and
     each field subgrids onto them. That is what keeps the controls on one line
     across the two columns when one label wraps and the other does not, and it
     puts the spacing in the row rather than in per field margins. */
  .tpg-row{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:auto auto auto;column-gap:18px;margin-bottom:22px;}
  .tpg-row > .tpg-field{display:grid;grid-template-rows:subgrid;grid-row:span 3;align-content:start;margin-bottom:0;}
  .tpg-field{margin-bottom:22px;}

  /* FAQ accordion. */
  .tpg-faq{border-bottom:1px solid rgba(26,26,46,0.14);}
  .tpg-sum{list-style:none;cursor:pointer;display:flex;align-items:baseline;gap:14px;padding:16px 20px;transition:background .15s;}
  .tpg-sum--faq{padding:18px 20px;}
  .tpg-sum::-webkit-details-marker{display:none;}
  .tpg-sum:hover{background:rgba(26,26,46,0.04);}
  .tpg-sum:focus-visible{outline:2px solid #2563EB;outline-offset:-2px;}
  .tpg-sum:after{content:"+";margin-left:auto;font-family:'JetBrains Mono',monospace;font-size:15px;color:#2563EB;}
  .tpg-faq[open] .tpg-sum:after{content:"-";}

  .tpg-input:focus{outline:2px solid #2563EB;outline-offset:1px;border-color:#2563EB;}

  /* Dial code plus number as one field. The wrapper owns the border and the
     focus ring, the controls inside own neither. No overflow clipping here,
     or the open panel would be cut off at the field edge. */
  .tpg-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;}
  .tpg-phone{display:flex;align-items:stretch;height:48px;border:1px solid rgba(26,26,46,0.14);border-radius:4px;background:#F5F2EC;}
  .tpg-phone:focus-within{outline:2px solid #2563EB;outline-offset:1px;border-color:#2563EB;}
  .tpg-phone input:focus{outline:none;}

  /* Country dial code picker. */
  .tpg-cc{position:relative;flex:0 0 auto;display:flex;}
  .tpg-cc-btn{display:flex;align-items:center;gap:6px;width:96px;padding:0 10px;border:none;border-right:1px solid rgba(26,26,46,0.14);background:transparent;font-family:inherit;color:#1a1a2e;cursor:pointer;}
  .tpg-cc-btn:focus{outline:none;}
  .tpg-cc-dial{font-family:'JetBrains Mono',monospace;font-size:14px;}
  .tpg-chev{margin-left:auto;flex-shrink:0;color:rgba(26,26,46,0.66);}
  .tpg-flag{flex-shrink:0;display:block;border:1px solid rgba(26,26,46,0.22);}

  .tpg-cc-panel{position:absolute;top:calc(100% + 5px);left:0;z-index:20;width:300px;max-width:calc(100vw - 64px);max-height:280px;overflow-y:auto;background:#F5F2EC;border:1px solid #1a1a2e;border-radius:4px;}
  /* The panel holds focus while it is open. The blue ring stays on the
     .tpg-phone wrapper via :focus-within, so the panel adds none of its
     own and nothing moves. */
  .tpg-cc-panel:focus{outline:none;}
  .tpg-cc-row{display:flex;align-items:center;gap:10px;padding:9px 12px;font-size:15px;color:#1a1a2e;cursor:pointer;}
  .tpg-cc-name{flex:1;min-width:0;}
  .tpg-cc-code{font-family:'JetBrains Mono',monospace;font-size:13px;color:rgba(26,26,46,0.66);flex-shrink:0;}
  .tpg-cc-row--hi{background:#1a1a2e;color:#F5F2EC;}
  .tpg-cc-row--hi .tpg-cc-code{color:rgba(245,242,236,0.74);}
  .tpg-cc-row--hi .tpg-flag{border-color:rgba(245,242,236,0.35);}
  /* Error border. The WhatsApp field draws its border on the wrapper, so the
     colour goes there rather than on the input sitting inside it. */
  .tpg-phone--err{border-color:#EA580C;}
  .tpg-consent input{margin-top:3px;accent-color:#2563EB;flex-shrink:0;}
  .tpg-consent:focus-within{outline:2px solid #2563EB;outline-offset:2px;}

  @media (prefers-reduced-motion: reduce){
    html,body{scroll-behavior:auto;}
    .tpg-fade,.tpg-fade-2{animation:none;}
  }

  @media (max-width:920px){
    .tpg-sec{padding:56px 20px;}
    .tpg-sec--hero{padding:56px 20px 48px;}
    .tpg-facts{grid-template-columns:1fr 1fr;row-gap:22px;}
    .tpg-fact:nth-child(odd){border-left:none;padding-left:0;}
    .tpg-two{grid-template-columns:1fr;gap:32px;}
    .tpg-learn-list{column-count:1;}
    /* One column, so the subgrid tracks are dropped and each field stacks on
       its own again. Spacing comes from the row gap. */
    .tpg-row{grid-template-columns:1fr;grid-template-rows:none;row-gap:22px;}
    .tpg-row > .tpg-field{display:block;grid-row:auto;}
  }

  @media (max-width:600px){
    .tpg-main{font-size:16px;}
    .tpg-sec{padding:48px 18px;}
    .tpg-sec--hero{padding:44px 18px 40px;}
    .tpg-fact{padding:2px 16px;}
    .tpg-box{padding:22px;}
    .tpg-box--lg{padding:22px;}
    .tpg-get{grid-template-columns:1fr;row-gap:8px;padding:26px 0;}
    .tpg-cc-panel{width:284px;}
    .tpg-days{gap:5px;}
    .tpg-day{font-size:11px;}
    .tpg-learn{padding:22px;}
    .tpg-learn-item{font-size:15.5px;padding-left:26px;}
    .tpg-sum{padding:14px 16px;gap:10px;}
    .tpg-sum--faq{padding:16px 16px;}
  }
`;
